/**
 * A portal's page for a client (#15170, match-mockup spec section 3), under
 * its title row (the 30px logo, the name, "Open portal ↗"):
 *
 * - "Managers on this portal" (muted "<n> managers"): Manager | Last 7 days |
 *   Newest document; a row opens the manager's page.
 * - Facts: Last checked | This week | Last 30 days | All time.
 * - Activity: a Day / Week / Month toggle, the period's sentence, and its
 *   documents per day as bars (today's solid, the rest lighter).
 * - "Activity" (muted "Days with nothing new are not listed"): one row per
 *   day that brought documents in: Checked | Brought in | Managers; a row
 *   expands to its documents (manager, type, "dated <date>", Open). The
 *   newest starts open.
 *
 * No state, no errors, no failures: a client's page lists only portals that
 * work. The counts come from the services read; the documents from the
 * page's own documents search.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CaretRightIcon } from "@phosphor-icons/react";
import type {
  DeliveredManager,
  PortalService,
} from "@/components/FpFundBrain/Manage/Connections/InvestorPortalsRow";
import { Segmented } from "@/components/FpPages/RMS/design";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import {
  formatCalendarDay,
  formatInstant,
  formatInstantTime,
  previousCalendarDay,
  todayInZone,
} from "@/utils/temporal";
import { Section } from "./HomeSections";
import {
  InlineLink,
  ROW_INTERACTION,
  listTd,
  listTh,
  managerPageHref,
  numTd,
} from "./PortalsOverview";
import { rowActivationProps } from "./rowActivation";
import type { DocumentRow } from "./browser/api";
import { TypeTagFor } from "./browser/DocumentsTable";
import {
  arrivalDayLabel,
  coverageRows,
  type CoverageManager,
} from "./browser/portals";

type Period = "day" | "week" | "month";

const PERIODS: { key: Period; label: string }[] = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
];

const PERIOD_DAYS: Record<Period, number> = { day: 1, week: 7, month: 30 };
/** A manager's documents in each period, as the services read counts them
 *  (calendar days in the viewer's zone, as `PERIOD_DAYS` lists them). */
const PERIOD_COUNT: Record<Period, (manager: DeliveredManager) => number> = {
  day: (manager) => manager.day_document_count,
  week: (manager) => manager.recent_document_count,
  month: (manager) => manager.month_document_count,
};
const PERIOD_LEAD: Record<Period, string> = {
  day: "Today",
  week: "This week",
  month: "Last 30 days",
};

const plural = (n: number, noun: string): string =>
  `${n.toLocaleString()} ${noun}${n === 1 ? "" : "s"}`;

/** The calendar days of a period, oldest first, ending today. */
const periodDays = (today: string, days: number): string[] => {
  const out: string[] = [];
  let day: string | null = today;
  for (let i = 0; i < days && day !== null; i += 1) {
    out.unshift(day);
    day = previousCalendarDay(day);
  }
  return out;
};

/** "7:20 AM today", "6:58 AM yesterday", else the day. */
const lastCheckedFact = (
  service: PortalService,
  zone: string,
  now: Date
): { big: string; small: string } => {
  const at = service.last_collected_at;
  if (at === null) return { big: "—", small: "" };
  if (at.granularity !== "instant") return { big: at.day, small: "" };
  const day = formatInstant(at.at, zone);
  const today = todayInZone(zone, now);
  const time = formatInstantTime(at.at, zone);
  if (day === today) return { big: time, small: "today" };
  if (day === previousCalendarDay(today))
    return { big: time, small: "yesterday" };
  return { big: formatInstant(at.at, zone, "monthDay"), small: time };
};

const Fact: React.FC<{ label: string; big: string; small?: string }> = ({
  label,
  big,
  small,
}) => (
  <div className="flex min-w-0 flex-col gap-1 px-4 py-3">
    <span className="text-[11px] font-medium uppercase tracking-[0.04em] text-[var(--fp-ink-3)]">
      {label}
    </span>
    <span className="text-[18px] font-semibold text-[var(--fp-ink)] [font-variant-numeric:tabular-nums]">
      {big}
      {small ? (
        <small className="ml-1 text-[12px] font-normal text-[var(--fp-ink-3)]">
          {small}
        </small>
      ) : null}
    </span>
  </div>
);

/** The period's documents per day as bars: today's solid, the rest
 *  lighter. */
const Bars: React.FC<{ days: string[]; counts: Map<string, number> }> = ({
  days,
  counts,
}) => {
  const w = 520;
  const h = 140;
  const pad = 26;
  const values = days.map((d) => counts.get(d) ?? 0);
  const max = Math.max(...values, 1);
  const bw = (w - pad * 2) / Math.max(days.length, 1);
  const step = max <= 4 ? 1 : Math.ceil(max / 4);
  const grid: number[] = [];
  for (let v = 0; v <= max; v += step) grid.push(v);
  const labelEvery = days.length > 10 ? 5 : 1;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      role="img"
      aria-label="Documents brought in per day"
      className="h-auto w-full max-w-[560px]"
    >
      {grid.map((v) => {
        const y = h - pad - (v / max) * (h - pad * 2);
        return (
          <g key={v}>
            <line x1={pad} x2={w - 6} y1={y} y2={y} stroke="var(--fp-border)" />
            <text
              x={pad - 6}
              y={y + 3}
              textAnchor="end"
              fontSize="10"
              fill="var(--fp-ink-3)"
            >
              {v}
            </text>
          </g>
        );
      })}
      {days.map((day, i) => {
        const v = values[i] ?? 0;
        const bh = (v / max) * (h - pad * 2);
        const x = pad + i * bw + bw * 0.18;
        return (
          <g key={day} data-testid="bar" data-day={day} data-count={v}>
            <rect
              x={x}
              y={h - pad - bh}
              width={bw * 0.64}
              height={bh}
              rx={2}
              fill="var(--fp-accent)"
              opacity={i === days.length - 1 ? 1 : 0.55}
            />
            {(i % labelEvery === 0 || i === days.length - 1) && (
              <text
                x={x + bw * 0.32}
                y={h - 8}
                textAnchor="middle"
                fontSize="10"
                fill="var(--fp-ink-3)"
              >
                {days.length <= 7
                  ? formatCalendarDay(day, "weekdayName").slice(0, 3)
                  : formatCalendarDay(day, "monthDay")}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
};

interface ActivityDay {
  day: string;
  rows: DocumentRow[];
}

/** The loaded documents by the calendar day they arrived, newest first. */
const activityDays = (rows: DocumentRow[], zone: string): ActivityDay[] => {
  const days: ActivityDay[] = [];
  for (const row of rows) {
    if (row.uploadedAt === null) continue;
    const day = formatInstant(row.uploadedAt, zone);
    const last = days[days.length - 1];
    if (last && last.day === day) last.rows.push(row);
    else days.push({ day, rows: [row] });
  }
  return days;
};

const managersOf = (rows: DocumentRow[]): CoverageManager[] => {
  const seen = new Map<string, CoverageManager>();
  for (const row of rows)
    for (const e of row.linkedEntities)
      if (e.kind === "manager" && !seen.has(e.publicId))
        seen.set(e.publicId, { name: e.name, publicId: e.publicId });
  return [...seen.values()];
};

export interface PortalPageProps {
  service: PortalService;
  /** The portal's documents, newest first, from the page's search. */
  rows: DocumentRow[];
  typeLabels: Record<string, string>;
  onOpenFile: (row: DocumentRow) => void;
  /** The next page's sentinel, at the foot of the Activity table. */
  loadMore?: React.ReactNode;
}

const PortalPage: React.FC<PortalPageProps> = ({
  service,
  rows,
  typeLabels,
  onOpenFile,
  loadMore,
}) => {
  const zone = useViewerTimeZone();
  const navigate = useNavigate();
  const now = useMemo(() => new Date(), []);
  const today = todayInZone(zone, now);
  const [period, setPeriod] = useState<Period>("week");
  const openManager = (manager: CoverageManager) =>
    navigate(managerPageHref(manager));

  const pairs = coverageRows([service]).filter((r) => r.manager !== null);
  const counts = useMemo(
    () =>
      new Map(
        service.daily_counts.flatMap((d) =>
          d.day.granularity === "date"
            ? [[d.day.day, d.documents] as const]
            : []
        )
      ),
    [service.daily_counts]
  );
  const days = periodDays(today, PERIOD_DAYS[period]);
  // The sentence's total is the server's count for the period, the same the
  // facts strip shows, never a sum of the bars that could drift from it.
  const periodTotal =
    period === "week"
      ? service.recent_document_count
      : period === "month"
        ? service.month_document_count
        : (counts.get(today) ?? 0);
  // The managers with documents in the period, from the server's per-pair
  // totals on the same days, not from the documents this page loaded.
  const periodManagers = service.delivered_managers.filter(
    (manager) => PERIOD_COUNT[period](manager) > 0
  ).length;
  const checked = lastCheckedFact(service, zone, now);
  const activity = useMemo(() => activityDays(rows, zone), [rows, zone]);
  const [open, setOpen] = useState<Set<string> | null>(null);
  // The newest day starts open, until the reader picks another.
  const opened = open ?? new Set(activity[0] ? [activity[0].day] : []);
  const toggle = (day: string) =>
    setOpen(() => {
      const next = new Set(opened);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  const dayLabel = (day: string): string =>
    day === today
      ? "Today"
      : day === previousCalendarDay(today)
        ? "Yesterday"
        : formatCalendarDay(day, "monthDay");

  return (
    <div className="flex flex-col">
      <Section
        title="Managers on this portal"
        trailing={
          <span className="text-[12.5px] font-normal text-[var(--fp-ink-3)]">
            {plural(pairs.length, "manager")}
          </span>
        }
      >
        {pairs.length === 0 ? (
          <p className="m-0 px-4 text-[13px] text-[var(--fp-ink-3)]">
            No managers linked yet
          </p>
        ) : (
          <div className="px-4">
            <table className="w-full table-fixed border-collapse">
              <thead>
                <tr>
                  <th scope="col" className={`${listTh} w-[34%]`}>
                    Manager
                  </th>
                  <th scope="col" className={`${listTh} w-[14%] text-right`}>
                    Last 7 days
                  </th>
                  <th scope="col" className={listTh}>
                    Newest document
                  </th>
                </tr>
              </thead>
              <tbody>
                {pairs.map((pair) => {
                  const manager = pair.manager!;
                  return (
                    <tr
                      key={pair.key}
                      data-manager={manager.name}
                      className={`border-b border-[var(--fp-border)] ${ROW_INTERACTION}`}
                      {...rowActivationProps(() => openManager(manager))}
                    >
                      <td className={`${listTd} font-semibold`}>
                        <span className="block truncate">{manager.name}</span>
                      </td>
                      <td className={`${listTd} ${numTd} text-right`}>
                        {pair.weekCount.toLocaleString()}
                      </td>
                      <td className={listTd}>
                        {pair.newest === null ? (
                          <span className="text-[var(--fp-ink-3)]">
                            Nothing new this week
                          </span>
                        ) : (
                          <span className="flex min-w-0 items-baseline gap-1">
                            <span className="min-w-0 truncate">
                              {pair.newest.name}
                            </span>{" "}
                            <span className="shrink-0 text-[var(--fp-ink-3)]">
                              ·{" "}
                              {pair.newest.uploaded_at.granularity === "instant"
                                ? arrivalDayLabel(
                                    pair.newest.uploaded_at.at,
                                    zone,
                                    now
                                  )
                                : pair.newest.uploaded_at.day}
                            </span>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="px-4 pt-6">
        <div
          data-testid="portal-facts"
          className="grid grid-cols-2 divide-[var(--fp-border)] rounded-[10px] border border-[var(--fp-border)] sm:grid-cols-4 sm:divide-x"
        >
          <Fact label="Last checked" big={checked.big} small={checked.small} />
          <Fact
            label="This week"
            big={service.recent_document_count.toLocaleString()}
            small={
              service.recent_document_count === 1 ? "document" : "documents"
            }
          />
          <Fact
            label="Last 30 days"
            big={service.month_document_count.toLocaleString()}
          />
          <Fact
            label="All time"
            big={service.document_count.toLocaleString()}
          />
        </div>
      </div>

      <div className="px-4 pt-4">
        <div
          data-testid="portal-activity"
          className="flex flex-col gap-3 rounded-[10px] border border-[var(--fp-border)] p-4"
        >
          <div className="flex flex-wrap items-center gap-3">
            <Segmented
              aria-label="Period"
              size="compact"
              options={PERIODS}
              value={period}
              onChange={setPeriod}
            />
            <p
              data-testid="activity-sentence"
              className="m-0 text-[13px] text-[var(--fp-ink-2)]"
            >
              <b className="font-semibold text-[var(--fp-ink)]">
                {PERIOD_LEAD[period]}:
              </b>{" "}
              {plural(periodTotal, "document")} from{" "}
              {plural(periodManagers, "manager")}, checked every business day.
            </p>
          </div>
          <Bars days={days} counts={counts} />
        </div>
      </div>

      <Section
        title="Activity"
        trailing={
          <span className="text-[12.5px] font-normal text-[var(--fp-ink-3)]">
            Days with nothing new are not listed
          </span>
        }
      >
        {activity.length === 0 ? (
          <p className="m-0 px-4 text-[13px] text-[var(--fp-ink-3)]">
            Nothing brought in yet
          </p>
        ) : (
          <div className="px-4">
            <table className="w-full table-fixed border-collapse">
              <thead>
                <tr>
                  <th scope="col" className={`${listTh} w-[28px]`}>
                    <span className="sr-only">Open</span>
                  </th>
                  <th scope="col" className={`${listTh} w-[26%]`}>
                    Checked
                  </th>
                  <th scope="col" className={`${listTh} w-[16%] text-right`}>
                    Brought in
                  </th>
                  <th scope="col" className={listTh}>
                    Managers
                  </th>
                </tr>
              </thead>
              <tbody>
                {activity.map((entry) => {
                  const expanded = opened.has(entry.day);
                  const newestAt = entry.rows[0]?.uploadedAt ?? null;
                  return (
                    <React.Fragment key={entry.day}>
                      <tr
                        data-testid="activity-row"
                        data-day={entry.day}
                        aria-expanded={expanded}
                        className={`border-b border-[var(--fp-border)] ${ROW_INTERACTION}`}
                        {...rowActivationProps(() => toggle(entry.day))}
                      >
                        <td className={listTd}>
                          <CaretRightIcon
                            size={12}
                            aria-hidden
                            className={`text-[var(--fp-ink-3)] transition-transform ${
                              expanded ? "rotate-90" : ""
                            }`}
                          />
                        </td>
                        <td className={`${listTd} ${numTd}`}>
                          {dayLabel(entry.day)}
                          {newestAt
                            ? `, ${formatInstantTime(newestAt, zone)}`
                            : ""}
                        </td>
                        <td className={`${listTd} ${numTd} text-right`}>
                          {entry.rows.length.toLocaleString()} new
                        </td>
                        <td className={listTd}>
                          <span className="block truncate">
                            {managersOf(entry.rows).map((m, i) => (
                              <React.Fragment key={m.publicId}>
                                {i > 0 && ", "}
                                <InlineLink onOpen={() => openManager(m)}>
                                  {m.name}
                                </InlineLink>
                              </React.Fragment>
                            ))}
                          </span>
                        </td>
                      </tr>
                      {expanded && (
                        <tr data-testid="activity-files">
                          <td
                            colSpan={4}
                            className="border-b border-[var(--fp-border)] bg-[var(--fp-bg-well)] px-3 py-1"
                          >
                            <table className="w-full table-fixed border-collapse">
                              <tbody>
                                {entry.rows.map((row) => {
                                  const manager = row.linkedEntities.find(
                                    (e) => e.kind === "manager"
                                  );
                                  const type = row.documentTypes[0];
                                  return (
                                    <tr key={row.fileId} data-file={row.fileId}>
                                      <td className={`${listTd} w-[38%]`}>
                                        <span className="block truncate">
                                          {row.fileName}
                                        </span>
                                      </td>
                                      <td className={`${listTd} w-[20%]`}>
                                        {manager ? (
                                          <InlineLink
                                            onOpen={() =>
                                              openManager({
                                                name: manager.name,
                                                publicId: manager.publicId,
                                              })
                                            }
                                          >
                                            {manager.name}
                                          </InlineLink>
                                        ) : null}
                                      </td>
                                      <td className={`${listTd} w-[16%]`}>
                                        {type ? (
                                          <TypeTagFor
                                            typeKey={type}
                                            typeLabels={typeLabels}
                                          />
                                        ) : null}
                                      </td>
                                      <td
                                        className={`${listTd} ${numTd} text-[var(--fp-ink-3)]`}
                                      >
                                        {row.documentDate
                                          ? `dated ${formatCalendarDay(
                                              row.documentDate,
                                              "monthDay"
                                            )}`
                                          : ""}
                                      </td>
                                      <td
                                        className={`${listTd} w-[10%] text-right`}
                                      >
                                        <InlineLink
                                          onOpen={() => onOpenFile(row)}
                                        >
                                          Open
                                        </InlineLink>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {loadMore}
      </Section>
    </div>
  );
};

export default PortalPage;
